// web/src/components/records/useConveneUnseal.ts — the shared "Convene unseal"
// action used by the records dashboard, record detail, and the requests inbox.
//
// Convening opens a break-glass ceremony against a sealed record (POST
// /quorum/:secretId/request) and routes to /unseal/:id, handing the ceremony a
// requester-view context via router state (UnsealCeremony reads location.state).
// METADATA ONLY: the request carries the requester's PUBLIC key id + a justification
// recorded in the audit — never a shard, key, or plaintext (R11).
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Principal } from '../../../../shared/types';
import { api } from '../../api';
import { keyManager, type Envelope } from '../../crypto';
import type { CeremonyContext, CeremonyHolder } from '../ceremony/UnsealCeremony';
import { parseEnvelope } from '../ceremony/envelope';
import { createRequesterEphemeralKey } from '../ceremony/transport';
import { recordName } from '../../lib/records';

export interface ConveneTarget {
  id: string;
  description: string;
  thresholdK: number;
  totalN: number;
  holders?: CeremonyHolder[];
}

/** The one-time join code + PIN the opener must share with holders out-of-band. */
export interface ConveneShare {
  requestId: string;
  joinWords: string;
  pin: string;
  ceremony: CeremonyContext;
}

export interface ConveneState {
  busy: boolean;
  error: string | null;
  /** Open a ceremony. On success surfaces `share` (the code + PIN) rather than
   *  navigating immediately — the caller shows a "share these" panel, then calls
   *  `enter()` to route into the ceremony. */
  convene: (target: ConveneTarget, justification: string) => Promise<void>;
  /** The opener's one-time join code + PIN, present once a ceremony is open. */
  share: ConveneShare | null;
  /** Route into the open ceremony (after the opener has shared the code + PIN). */
  enter: () => void;
  /** Dismiss the share panel without entering (the ceremony stays open). */
  dismissShare: () => void;
}

/** Resolve the requester's local decryption key id (shards are re-encrypted to it
 *  for delivery). Falls back to the principal id when no local key exists yet — the
 *  server treats it as the requester's identifier in either case. */
async function requesterPubkeyId(principal: Principal): Promise<string> {
  try {
    const keys = await keyManager.listLocalKeys();
    if (keys.length > 0) return keys[0].keyId;
  } catch {
    /* IndexedDB unavailable (private mode); fall through to the principal id */
  }
  return principal.id;
}

export function useConveneUnseal(principal: Principal): ConveneState {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [share, setShare] = useState<ConveneShare | null>(null);

  const convene = useCallback(
    async (target: ConveneTarget, justification: string) => {
      setBusy(true);
      setError(null);
      try {
        const pubkeyId = await requesterPubkeyId(principal);

        // M3.2 — generate the requester's EPHEMERAL ECDH keypair in-browser and
        // publish ONLY its public key on the request. Holders re-encrypt their
        // decrypted share to it before delivery, so the server only ever relays an
        // opaque blob it cannot read. The private handle stays in this tab's memory
        // (never persisted/sent) and is carried into the ceremony to decrypt blobs.
        const ephemeral = await createRequesterEphemeralKey();
        const req = await api.requestUnseal(
          target.id,
          pubkeyId,
          justification,
          JSON.stringify(ephemeral.publicKey),
        );

        // Try to obtain the AES-GCM envelope (the encrypted-secret blob) the
        // requester needs to OPEN the record at quorum. It is resolved from the
        // record's envelopeMeta when present; today the backend does not deliver
        // the ciphertext (documented gap in ceremony/envelope.ts), so this is
        // null and the ceremony surfaces the gap honestly rather than faking a
        // reveal. Best-effort: a failure here must not block convening.
        let envelope: Envelope | undefined;
        try {
          const detail = await api.getRecord(target.id);
          envelope = parseEnvelope(detail) ?? undefined;
        } catch {
          /* read-path blip — the ceremony self-hydrates status either way */
        }

        const ceremony: CeremonyContext = {
          recordName: recordName(target),
          thresholdK: target.thresholdK,
          totalN: target.totalN,
          requesterLabel: principal.name,
          holders: target.holders ?? [],
          role: 'requester',
          envelope,
          requesterEphemeral: ephemeral,
        };
        // Surface the one-time join code + PIN so the opener can share them with
        // holders out-of-band; entering the ceremony is a second, explicit step.
        setShare({
          requestId: req.id,
          joinWords: req.joinWords ?? '',
          pin: req.pin,
          ceremony,
        });
        setBusy(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setBusy(false);
      }
    },
    [principal],
  );

  const enter = useCallback(() => {
    if (!share) return;
    navigate(`/unseal/${share.requestId}`, { state: { ceremony: share.ceremony } });
    setShare(null);
  }, [navigate, share]);

  const dismissShare = useCallback(() => setShare(null), []);

  return { busy, error, convene, share, enter, dismissShare };
}
